import { API } from "../api/builder";
import CryptoJS from "crypto-js";
import appSettings from "../appsettings.json";

const CONFIG_ENDPOINT = `${appSettings.ApiSettings.BaseUrl}api/authsp/jwk`;

export interface AppConfig {
  REACT_APP_API_BASE_URL: string;
  DRAGONFISH_SCIENTIFICNAME?: string;
  REACT_APP_API_APEX_SECRET?: string;
  REACT_APP_STRIPE_PUBLIC_KEY?: string;
  REACT_APP_AUTH_USER?: string;
  REACT_APP_AUTH_SECRET?: string;
  REACT_APP_ENC_SECRET_KEY?: string;
  REACT_APP_SITE_KEY?: string;
  REACT_APP_API_KEY?: string;
  REACT_APP_BASE_URL?: string;
  REACT_APP_API_APEX_BASE_URL?: string;
  REACT_APP_API_APEX_ACCOUNTID?: string;
  REACT_APP_UAT_AUTH_SCREEN?: string;
}

let _config: AppConfig | null = null;

export async function fetchConfig(): Promise<AppConfig> {
  if (_config) return _config;
  // Fetch runtime config via GET. Keep same timeout.
  const resp = await API.get(CONFIG_ENDPOINT, { timeout: 7000 });
  const data = resp?.data ?? {};

  // Helper: try to decrypt a Base64 AES-CBC PKCS7 ciphertext using provided key/iv.
  // The server may supply key/iv as Base64 strings or as plaintext strings whose UTF8
  // bytes are used by .NET. We'll attempt both parsing strategies for compatibility.
  const tryDecrypt = (cipherText: any, key?: string | null, iv?: string | null) => {
    if (!cipherText || !key || typeof cipherText !== "string") return cipherText;
    try {
      const cipherParams = CryptoJS.lib.CipherParams.create({ ciphertext: CryptoJS.enc.Base64.parse(cipherText) });

      const decryptWith = (keyWA: CryptoJS.lib.WordArray, ivWA?: CryptoJS.lib.WordArray) => {
        try {
          const dec = CryptoJS.AES.decrypt(cipherParams, keyWA, {
            iv: ivWA,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          });
          const plain = dec.toString(CryptoJS.enc.Utf8);
          return plain && plain.length > 0 ? plain : null;
        } catch (e) {
          return null;
        }
      };

      // Try Base64-decoded key/iv first
      let keyBase64: CryptoJS.lib.WordArray | null = null;
      let ivBase64: CryptoJS.lib.WordArray | undefined = undefined;
      try { keyBase64 = CryptoJS.enc.Base64.parse(key); } catch { keyBase64 = null; }
      if (iv) {
        try { ivBase64 = CryptoJS.enc.Base64.parse(iv); } catch { ivBase64 = undefined; }
      }
      if (keyBase64) {
        const res = decryptWith(keyBase64, ivBase64);
        if (res) return res;
      }

      // Fallback: treat key/iv as UTF8-encoded strings (Encoding.UTF8.GetBytes on server)
      const keyUtf8 = CryptoJS.enc.Utf8.parse(key);
      const ivUtf8 = iv ? CryptoJS.enc.Utf8.parse(iv) : undefined;
      const res2 = decryptWith(keyUtf8, ivUtf8);
      if (res2) return res2;

      return cipherText;
    } catch (e) {
      return cipherText;
    }
  };

  const encKey = data.confiG_KEY;
  const encIV = data.confiG_VAL;

  _config = {
    REACT_APP_API_BASE_URL: tryDecrypt(data.reacT_APP_API_BASE_URL, encKey, encIV),
    REACT_APP_API_KEY: tryDecrypt(data.reacT_APP_API_KEY, encKey, encIV),
    REACT_APP_BASE_URL: tryDecrypt(data.reacT_APP_BASE_URL, encKey, encIV),
    REACT_APP_API_APEX_BASE_URL: tryDecrypt(data.reacT_APP_API_APEX_BASE_URL, encKey, encIV),
    REACT_APP_API_APEX_SECRET: tryDecrypt(data.reacT_APP_API_APEX_SECRET, encKey, encIV),
    REACT_APP_STRIPE_PUBLIC_KEY: tryDecrypt(data.reacT_APP_STRIPE_PUBLIC_KEY, encKey, encIV),
    REACT_APP_API_APEX_ACCOUNTID: tryDecrypt(data.reacT_APP_API_APEX_ACCOUNTID, encKey, encIV),
    REACT_APP_AUTH_USER: tryDecrypt(data.reacT_APP_AUTH_USER, encKey, encIV),
    REACT_APP_AUTH_SECRET: tryDecrypt(data.reacT_APP_AUTH_SECRET, encKey, encIV),
    REACT_APP_UAT_AUTH_SCREEN: data.reacT_APP_UAT_AUTH_SCREEN,
    REACT_APP_ENC_SECRET_KEY: tryDecrypt(data.reacT_APP_ENC_SECRET_KEY, encKey, encIV),
    REACT_APP_SITE_KEY:  tryDecrypt(data.reacT_APP_SITE_KEY, encKey, encIV),
    DRAGONFISH_SCIENTIFICNAME: data.dragonfisH_SCIENTIFICNAME,
  };

  if (!_config.REACT_APP_API_BASE_URL) {
    throw new Error('Missing REACT_APP_API_BASE_URL in config response');
  }

  return _config;
}

export function getConfig(): AppConfig {
  if (!_config) throw new Error('Config not loaded');
  return _config;
}
